import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import {
  Clock, Play, Square, Coffee, LogOut, Loader2, ShieldCheck, MonitorUp,
  Camera, AlertTriangle, CheckCircle2, ListTodo, Phone,
} from 'lucide-react';
import { VATasksPanel, VACallListPanel, taskDueBucket } from '../components/VAWorkPanels';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'utmost_va_token';
const SHOT_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

const fmtDur = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const api = async (path, { method = 'GET', token, body, form } = {}) => {
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API}/api/timetrack${path}`, {
    method, headers, body: form || (body ? JSON.stringify(body) : undefined),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || `Request failed (${res.status})`);
  }
  return res.status === 204 ? null : res.json();
};

// ---- Login ----
const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setLoading(true);
    try {
      const d = await api('/login', { method: 'POST', body: { email, password } });
      localStorage.setItem(TOKEN_KEY, d.token);
      onLogin(d.token, d.staff);
    } catch (e2) { setErr(e2.message); } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0A2A43] to-[#0d3a5c] px-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md" data-testid="va-login-form">
        <div className="flex items-center gap-2 mb-1"><Clock className="h-6 w-6 text-[#0A2A43]" /><h1 className="text-2xl font-bold text-[#0A2A43]">Team Time Tracker</h1></div>
        <p className="text-sm text-gray-500 mb-6">Utmost Financial — staff sign in</p>
        {err && <div className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2" data-testid="va-login-error">{err}</div>}
        <label className="block mb-3"><span className="text-xs font-medium text-gray-600">Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="va-email"
            className="mt-1 w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0A2A43]" /></label>
        <label className="block mb-5"><span className="text-xs font-medium text-gray-600">Password</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="va-password"
            className="mt-1 w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0A2A43]" /></label>
        <Button type="submit" disabled={loading} className="w-full bg-[#0A2A43] hover:bg-[#0d3a5c] text-white" data-testid="va-login-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Sign In
        </Button>
      </form>
    </div>
  );
};

// ---- Tracker ----
const Tracker = ({ token, staff, onLogout }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [task, setTask] = useState('');
  const [tick, setTick] = useState(0);
  const [sharing, setSharing] = useState(false);
  const [shareMsg, setShareMsg] = useState('');
  const [lastShot, setLastShot] = useState(null);
  const [err, setErr] = useState('');
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const [view, setView] = useState('tracker'); // tracker | tasks | calllist
  const [dueBadge, setDueBadge] = useState(0);

  // Lightweight fetch for the "My Tasks" nav badge (overdue + due-today, not done).
  useEffect(() => {
    let cancelled = false;
    api('/my-tasks', { token }).then((tasks) => {
      if (cancelled) return;
      const n = (tasks || []).filter((t) => t.status !== 'done' && ['overdue', 'today'].includes(taskDueBucket(t.due_date))).length;
      setDueBadge(n);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [token, view]);

  const load = useCallback(async () => {
    try {
      const s = await api('/current', { token });
      setSession(s);
      if (s) setTask(s.current_task || '');
    } catch (e) {
      if (String(e.message).toLowerCase().includes('expired') || String(e.message).includes('401')) onLogout();
      else setErr(e.message);
    } finally { setLoading(false); }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, []);

  // live elapsed
  const metrics = (() => {
    if (!session) return { worked: 0, onBreak: false };
    const clockIn = new Date(session.clock_in).getTime();
    let breakMs = 0, onBreak = false;
    (session.breaks || []).forEach((b) => {
      const bs = b.start ? new Date(b.start).getTime() : null;
      const be = b.end ? new Date(b.end).getTime() : null;
      if (bs && be) breakMs += be - bs;
      else if (bs && !be) { onBreak = true; breakMs += Date.now() - bs; }
    });
    const worked = Math.max(0, (Date.now() - clockIn - breakMs) / 1000);
    return { worked, onBreak };
  })();

  const captureAndUpload = useCallback(async () => {
    const stream = streamRef.current;
    if (!stream) return;
    const trackObj = stream.getVideoTracks()[0];
    if (!trackObj || trackObj.readyState !== 'live') return;
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    await new Promise((resolve) => canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          const fd = new FormData(); fd.append('file', blob, 'screenshot.jpg');
          await api('/screenshot', { method: 'POST', token, form: fd });
          setLastShot(new Date());
        } catch (e) { console.warn('Screenshot upload failed (transient — will retry next interval):', e); }
      }
      resolve();
    }, 'image/jpeg', 0.6));
  }, [token]);

  const stopShare = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setSharing(false);
  }, []);

  const startShare = useCallback(async () => {
    setShareMsg('');
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 1 }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
      setSharing(true);
      stream.getVideoTracks()[0].addEventListener('ended', () => { setSharing(false); streamRef.current = null; setShareMsg('Screen sharing stopped. Restart it to keep monitoring active.'); });
      setTimeout(captureAndUpload, 4000);
    } catch (e) {
      setShareMsg('Screen sharing was declined. Your hours are still tracked, but screenshots will not be captured until you enable it.');
    }
  }, [captureAndUpload]);

  // periodic screenshot every 30 min while clocked in + sharing
  useEffect(() => {
    if (!session || !sharing) return undefined;
    const id = setInterval(() => { if (!metrics.onBreak) captureAndUpload(); }, SHOT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [session, sharing, captureAndUpload]);

  const act = async (fn) => { setBusy(true); setErr(''); try { const s = await fn(); if (s !== undefined) { setSession(s); if (s) setTask(s.current_task || ''); } } catch (e) { setErr(e.message); } finally { setBusy(false); } };

  const clockIn = () => act(async () => { const s = await api('/clock-in', { method: 'POST', token, body: { task } }); await startShare(); return s; });
  const clockOut = () => act(async () => { await api('/clock-out', { method: 'POST', token }); stopShare(); return null; });
  const toggleBreak = () => act(() => api(metrics.onBreak ? '/break/end' : '/break/start', { method: 'POST', token }));
  const saveTask = () => act(() => api('/task', { method: 'POST', token, body: { task } }));

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0A2A43]" /></div>;
  const active = !!session;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="va-tracker">
      <video ref={videoRef} muted playsInline style={{ display: 'none' }} />
      <div className="bg-[#0A2A43] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2"><Clock className="h-5 w-5" /><span className="font-semibold">Team Time Tracker</span></div>
        <div className="flex items-center gap-3 text-sm">
          <span className="opacity-90">{staff?.name}</span>
          <button onClick={() => { stopShare(); onLogout(); }} className="flex items-center gap-1 opacity-90 hover:opacity-100" data-testid="va-logout"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </div>

      {/* Section navigation */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-2xl mx-auto px-2 flex gap-1">
          {[['tracker', 'Tracker', Clock], ['tasks', 'My Tasks', ListTodo], ['calllist', 'Call List', Phone]].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setView(id)} data-testid={`va-nav-${id}`}
              className={`relative flex items-center gap-1.5 px-4 py-3 text-sm font-medium -mb-px border-b-2 transition-colors ${view === id ? 'border-[#0A2A43] text-[#0A2A43]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              <Icon className="h-4 w-4" /> {label}
              {id === 'tasks' && dueBadge > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold" data-testid="va-nav-tasks-badge">{dueBadge}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {view === 'tasks' && <div className="max-w-2xl mx-auto p-4"><VATasksPanel token={token} /></div>}
      {view === 'calllist' && <div className="max-w-2xl mx-auto p-4"><VACallListPanel token={token} /></div>}

      {view === 'tracker' && (
      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Consent banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-sm text-amber-800" data-testid="va-consent-banner">
          <ShieldCheck className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>Your work hours and periodic screen screenshots (every 30 minutes) are recorded while you are clocked in, so your manager can review your activity. By clocking in you consent to this monitoring.</span>
        </div>

        {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2">{err}</div>}

        {/* Status card */}
        <div className={`rounded-2xl p-6 text-center shadow-sm ${active ? 'bg-[#0A2A43] text-white' : 'bg-white'}`} data-testid="va-status-card">
          <p className={`text-xs uppercase tracking-wide ${active ? 'text-blue-200' : 'text-gray-400'}`}>
            {active ? (metrics.onBreak ? 'On Break' : 'Clocked In') : 'Clocked Out'}
          </p>
          <div className="text-5xl font-bold my-2 tabular-nums" data-testid="va-timer">{fmtDur(metrics.worked)}</div>
          <p className={`text-xs ${active ? 'text-blue-200' : 'text-gray-400'}`}>worked today {active && session.clock_in ? `· since ${new Date(session.clock_in).toLocaleTimeString('en-US', { timeZone: 'America/Winnipeg', hour: '2-digit', minute: '2-digit' })} CST` : ''}</p>

          <div className="flex items-center justify-center gap-3 mt-5">
            {!active ? (
              <Button onClick={clockIn} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg rounded-xl" data-testid="va-clock-in-btn">
                {busy ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Play className="h-5 w-5 mr-2" />} Clock In
              </Button>
            ) : (
              <>
                <Button onClick={toggleBreak} disabled={busy} variant="outline" className="px-6 py-6 rounded-xl border-white/40 text-black" data-testid="va-break-btn">
                  <Coffee className="h-5 w-5 mr-2" /> {metrics.onBreak ? 'End Break' : 'Take Break'}
                </Button>
                <Button onClick={clockOut} disabled={busy} className="bg-rose-500 hover:bg-rose-600 text-white px-8 py-6 text-lg rounded-xl" data-testid="va-clock-out-btn">
                  {busy ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Square className="h-5 w-5 mr-2" />} Clock Out
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Daily notes */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <label className="block text-xs font-medium text-gray-600 mb-1">My notes for today</label>
          <p className="text-[11px] text-gray-400 mb-2">Jot down anything about your day — tasks, progress, blockers. Your manager can see these notes.</p>
          <textarea value={task} onChange={(e) => setTask(e.target.value)} rows={5}
            placeholder={"e.g.\n- Added client emails to the call list\n- Followed up with 12 leads\n- Waiting on info for the Smith file"}
            data-testid="va-task-input" className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0A2A43] resize-y" />
          <div className="flex items-center justify-between mt-2">
            {!active ? <p className="text-[11px] text-gray-400">Clock in to write your notes.</p> : <span />}
            <Button onClick={saveTask} disabled={busy || !active} variant="outline" data-testid="va-task-save">Save notes</Button>
          </div>
        </div>

        {/* Screen monitoring status */}
        {active && (
          <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="va-monitor-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                {sharing ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <AlertTriangle className="h-5 w-5 text-amber-500" />}
                <span className="font-medium text-gray-700">{sharing ? 'Screen monitoring active' : 'Screen monitoring off'}</span>
              </div>
              {!sharing && <Button size="sm" onClick={startShare} className="bg-[#0A2A43] text-white" data-testid="va-enable-share"><MonitorUp className="h-4 w-4 mr-1" /> Enable</Button>}
              {sharing && <Button size="sm" variant="outline" onClick={captureAndUpload} data-testid="va-capture-now"><Camera className="h-4 w-4 mr-1" /> Capture now</Button>}
            </div>
            {shareMsg && <p className="text-[11px] text-amber-700 mt-2">{shareMsg}</p>}
            {lastShot && <p className="text-[11px] text-gray-400 mt-2">Last screenshot: {lastShot.toLocaleTimeString('en-US', { timeZone: 'America/Winnipeg', hour: '2-digit', minute: '2-digit' })} CST</p>}
            <p className="text-[11px] text-gray-400 mt-1">Screenshots are captured automatically every 30 minutes. Keep this tab open while working.</p>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default function VATracker() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [staff, setStaff] = useState(null);
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    api('/me', { token }).then(setStaff).catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }).finally(() => setChecking(false));
  }, [token]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); setStaff(null); };

  if (checking) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0A2A43]" /></div>;
  if (!token || !staff) return <Login onLogin={(t, s) => { setToken(t); setStaff(s); }} />;
  return <Tracker token={token} staff={staff} onLogout={logout} />;
}
